import { useEffect, useRef, useState } from 'react';
import { getResultCoverConfig, getTitleParts } from '../data/result-cover-config';
import themeData from '../data/theme-tokens.json';
import type { Hero, MatchResult } from '../types';

type Props = { hero: Hero; result: MatchResult; onClose: () => void };

function loadImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

function drawFlower(context: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) {
  context.save();
  context.translate(x, y);
  context.fillStyle = color;
  for (let index = 0; index < 4; index += 1) {
    context.rotate(Math.PI / 2);
    context.beginPath();
    context.ellipse(0, -size * .62, size * .27, size * .60, 0, 0, Math.PI * 2);
    context.fill();
  }
  context.fillStyle = '#d0b67c';
  context.beginPath();
  context.arc(0, 0, size * .18, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawOrnateRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, options: { fill: string | CanvasGradient; stroke: string; radius: number }) {
  context.save();
  context.fillStyle = options.fill;
  context.strokeStyle = options.stroke;
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(x, y, width, height, options.radius);
  context.fill();
  context.stroke();
  context.globalAlpha = .55;
  context.beginPath();
  context.roundRect(x + 8, y + 8, width - 16, height - 16, Math.max(4, options.radius - 8));
  context.stroke();
  context.globalAlpha = 1;
  drawFlower(context, x + 7, y + height / 2, 15, '#f6edf0');
  drawFlower(context, x + width - 7, y + height / 2, 15, '#f6edf0');
  context.restore();
}

function drawVerticalTitle(context: CanvasRenderingContext2D, title: string, x: number, y: number, size: number, color: string) {
  context.save();
  context.textAlign = 'center';
  context.textBaseline = 'top';
  context.fillStyle = color;
  context.font = `400 ${size}px "Hero Brush", "STKaiti", serif`;
  [...title].forEach((character, index) => context.fillText(character, x, y + index * size * .78));
  context.restore();
}

function drawImageContained(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  context.drawImage(image, x, y, drawWidth, drawHeight);
}

function drawCinematicPoster(context: CanvasRenderingContext2D, artwork: HTMLImageElement, titleAsset: HTMLImageElement | null, hero: Hero, result: MatchResult) {
  const config = getResultCoverConfig(hero);
  const theme = themeData[hero.theme];
  const displayColor = config.lightArtwork ? theme.primary : '#f6ead4';
  const inkColor = theme.ink;
  const frameColor = theme.accent;

  context.filter = config.lightArtwork ? 'brightness(1.04) saturate(.91) contrast(.98)' : 'saturate(.96) contrast(1.01)';
  if (hero.id === 20) context.filter = 'brightness(1.07) saturate(.88) contrast(.98)';
  context.drawImage(artwork, 0, 0, 900, 1600);
  context.filter = 'none';

  const bottomShade = context.createLinearGradient(0, 1080, 0, 1600);
  bottomShade.addColorStop(0, 'rgba(57,50,78,0)');
  bottomShade.addColorStop(1, config.lightArtwork ? 'rgba(43,37,61,.42)' : 'rgba(18,14,25,.72)');
  context.fillStyle = bottomShade;
  context.fillRect(0, 1000, 900, 600);

  context.textAlign = 'center';
  context.fillStyle = displayColor;
  context.font = '600 47px "Songti SC", "STSong", serif';
  context.fillText('✿  你的盖世英雄  ✿', 450, 94);

  if (titleAsset) {
    context.save();
    context.globalAlpha = .94;
    context.shadowColor = config.lightArtwork ? 'rgba(255,255,255,.82)' : 'rgba(20,10,13,.72)';
    context.shadowBlur = config.lightArtwork ? 7 : 10;
    context.shadowOffsetY = 3;
    drawImageContained(context, titleAsset, 24, 145, hero.name.length >= 4 ? 250 : 306, 500);
    context.restore();
  } else if (config.titleLayout === 'horizontal') {
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillStyle = displayColor;
    context.font = `400 ${hero.name.length > 3 ? 128 : 145}px "Hero Brush", "STKaiti", serif`;
    context.fillText(hero.name, 46, 156);
  } else if (config.titleLayout === 'dual') {
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillStyle = displayColor;
    context.font = '400 119px "Hero Brush", "STKaiti", serif';
    getTitleParts(hero).forEach((part, index) => context.fillText(part, 42 + index * 68, 142 + index * 105));
  } else {
    drawVerticalTitle(context, hero.name, hero.name.length === 2 ? 142 : 131, 145, hero.name.length === 2 ? 156 : 138, displayColor);
  }

  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillStyle = displayColor;
  context.font = '500 34px "Songti SC", "STSong", serif';
  context.fillText('命定契合度', 72, 683);
  context.font = '400 170px Didot, "Times New Roman", serif';
  context.fillStyle = config.lightArtwork ? theme.primary : '#f3d1c4';
  context.fillText(String(result.displayMatch), 67, 832);
  const scoreWidth = context.measureText(String(result.displayMatch)).width;
  context.font = '400 68px Didot, "Times New Roman", serif';
  context.fillText('%', 67 + scoreWidth + 5, 827);
  const rule = context.createLinearGradient(70, 0, 325, 0);
  rule.addColorStop(0, 'rgba(107,90,145,0)');
  rule.addColorStop(.25, theme.primary);
  rule.addColorStop(.65, frameColor);
  rule.addColorStop(1, 'rgba(205,187,145,0)');
  context.fillStyle = rule;
  context.fillRect(68, 846, 270, 2);

  context.textAlign = 'center';
  hero.keywords.forEach((keyword, index) => {
    const y = 875 + index * 82;
    drawOrnateRect(context, 46, y, 300, 66, { fill: 'rgba(255,253,250,.93)', stroke: frameColor, radius: 25 });
    context.fillStyle = inkColor;
    context.font = '500 34px "Songti SC", "STSong", serif';
    context.fillText(keyword, 196, y + 44);
  });

  drawOrnateRect(context, 38, 1130, 824, 216, { fill: 'rgba(255,252,248,.95)', stroke: frameColor, radius: 34 });
  drawFlower(context, 45, 1150, 22, '#f3e8ee');
  drawFlower(context, 855, 1326, 22, '#f3e8ee');
  context.fillStyle = theme.primary;
  context.font = '700 76px Georgia, serif';
  context.fillText('“', 93, 1196);
  context.fillText('”', 805, 1325);
  context.fillStyle = inkColor;
  context.font = '500 34px "Songti SC", "STSong", serif';
  const lines = config.quoteLines;
  const lineHeight = lines.length === 2 ? 66 : 54;
  const firstLineY = lines.length === 2 ? 1225 : 1197;
  lines.forEach((line, index) => context.fillText(line, 450, firstLineY + index * lineHeight));

  const saveGradient = context.createLinearGradient(46, 1372, 424, 1492);
  saveGradient.addColorStop(0, theme.primary);
  saveGradient.addColorStop(1, theme.secondary);
  drawOrnateRect(context, 40, 1374, 394, 112, { fill: saveGradient, stroke: frameColor, radius: 42 });
  drawOrnateRect(context, 466, 1374, 394, 112, { fill: 'rgba(255,252,248,.96)', stroke: frameColor, radius: 42 });
  context.font = '500 43px "Songti SC", "STSong", serif';
  context.fillStyle = '#fffaf4';
  context.fillText('保存结果', 237, 1445);
  context.fillStyle = inkColor;
  context.fillText('查看完整报告', 663, 1445);
}

export function PosterModal({ hero, result, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const targetCanvas = canvas;
    const targetContext = context;
    setReady(false);
    let cancelled = false;

    async function draw() {
      const config = getResultCoverConfig(hero);
      const [image, titleAsset] = await Promise.all([
        loadImage(hero.result_bg),
        config.titleAsset ? loadImage(config.titleAsset) : Promise.resolve(null),
        document.fonts.load('120px "Hero Brush"'),
      ]);
      if (cancelled) return;
      targetCanvas.width = 900;
      targetCanvas.height = 1600;
      targetContext.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
      drawCinematicPoster(targetContext, image, titleAsset, hero, result);
      setReady(true);
    }

    void draw();
    return () => { cancelled = true; };
  }, [hero, result.displayMatch]);

  function download() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `我的盖世英雄-${hero.name}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="分享海报预览">
      <div className="poster-dialog">
        <div className="poster-toolbar"><strong>分享海报</strong><button type="button" onClick={onClose} aria-label="关闭">×</button></div>
        <canvas ref={canvasRef} className="poster-canvas" />
        <button type="button" className="primary-button" onClick={download} disabled={!ready}>保存图片</button>
      </div>
    </div>
  );
}
